import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import { Store } from "@ngrx/store";
import { CookieService, generateVersion4GUID, HttpClient, HttpClientFactory, RequestPayload } from "@obg/ngx.global";
import { Log, LoggingFactory, METRICS_CLIENT_HTTP, MetricsClient, PlatformService } from "@obg/ngx.global.evo";
import { isEmpty, pickBy, pull, some, uniq } from "lodash-es";
import { Observable, of, throwError, TimeoutError } from "rxjs";
import { catchError, share, switchMap, tap, timeout } from "rxjs/operators";

import { AppContextConfigService } from "../app-context/app-context.config";
import { AppError } from "../app-error/app-error.model";
import { AppErrorService } from "../app-error/app-error.service";
import { AUTH_MESSAGE_CODES } from "../auth/auth.const";
import { CoreConfig } from "../config/core-config";
import { MarketConfigService } from "../market/market.config";
import { CoreState } from "../state";

import {
	OBG_EXPERIENCE_HEADER_NAME,
	OBG_EXPERIENCE_SOURCE,
	OBG_EXPERIMENTS_HEADER_NAME,
	OBG_EXPERIMENTS_SOURCE,
} from "./http-client.const";
import { HttpErrorResponse as ApiErrorResponse, HttpInterceptorType, RequestInterceptor } from "./http-client.model";

interface HttpMethod {
	<T>(payload?: RequestPayload): Observable<T>;
}

/**
 * WARNING: inform fe-evolution team if making any changes
 *
 * There is new microfrontend-compatible `CoreHttpClient` in `ngx.global.evo` that needs to be kept in sync.
 * This client will be slowly phased out in favor of new client from `ngx.global.evo`
 */
@Injectable({
	providedIn: "root",
})
export class CoreHttpClient implements HttpClient {
	private defaultHeaders: Record<string, string | string[]>;
	private interceptors: Record<string, RequestInterceptor[]> = {};
	private httpClient: HttpClient;

	private logger: Log;

	constructor(
		private marketConfig: MarketConfigService,
		private coreConfig: CoreConfig,
		private httpClientFactory: HttpClientFactory,
		private store: Store<CoreState>,
		private appErrorService: AppErrorService,
		private cookieService: CookieService,
		private platformService: PlatformService,
		loggingFactory: LoggingFactory,
		private appContextConfigService: AppContextConfigService,
		@Optional() @Inject(METRICS_CLIENT_HTTP) private metrics: MetricsClient
	) {
		this.logger = loggingFactory.get("CoreHttpClient");
		this.httpClient = this.httpClientFactory.get({ url: appContextConfigService.getApiBaseUri() });
		this.defaultHeaders = {
			"Content-Type": "application/json",
			brandId: appContextConfigService.getApiToken(),
			marketCode: this.marketConfig.getLanguageCode(),
		};
	}

	setDefaultHeader(key: string, value: string | string[]) {
		this.defaultHeaders[key] = value;
	}

	deleteDefaultHeader(key: string) {
		delete this.defaultHeaders[key];
	}

	get<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.get.bind(this.httpClient), payload);
	}

	getRaw<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.getRaw.bind(this.httpClient), payload);
	}

	post<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.post.bind(this.httpClient), payload);
	}

	put<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.put.bind(this.httpClient), payload);
	}

	delete<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.delete.bind(this.httpClient), payload);
	}

	patch<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.patch.bind(this.httpClient), payload);
	}

	head<T>(payload: RequestPayload): Observable<T> {
		return this.process<T>(this.httpClient.head.bind(this.httpClient), payload);
	}

	/**
	 * Adds a hook to the http pipeline.
	 * This can be used, for example, to benchmark http calls.
	 *
	 * Returns a function that can be called to remove the interceptor.
	 */
	addInterceptor(interceptor: RequestInterceptor, interceptorType = HttpInterceptorType.AfterRequest): () => void {
		if (!this.interceptors[interceptorType]) {
			this.interceptors[interceptorType] = [];
		}
		this.interceptors[interceptorType].push(interceptor);

		return () => {
			pull(this.interceptors[interceptorType], interceptor);
		};
	}

	// todo: OBGB-6215 change `any` to `never` and fix type errors as that is the correct return type
	private handleError(
		payload: RequestPayload,
		res: HttpErrorResponse,
		httpCallerOriginalStackTrace: any,
		durationInMs: number
	) {
		if (!some(payload.suppressErrorLogging, (err) => err === res.status)) {
			if (this.generateLogMessage(res) !== AUTH_MESSAGE_CODES.singleSignOn.siteCoreCookieNotInHeader) {
				this.logger.error("handleError", this.generateLogMessage(res), {
					baseUrl: this.appContextConfigService.getApiBaseUri(),
					requestPayload: payload,
					response: res,
					httpCallerOriginalStackTrace,
					durationInMs,
				});
			} else {
				this.logger.warn("handleWarning", this.generateLogMessage(res), {
					baseUrl: this.appContextConfigService.getApiBaseUri(),
					requestPayload: payload,
					response: res,
					httpCallerOriginalStackTrace,
					durationInMs,
				});
			}
		}
		let errorPayload: any;

		try {
			errorPayload = this.generateErrorMsg(res);
		} catch {
			errorPayload = res.error;
		}

		const error: AppError = {
			type: res.status,
			code: errorPayload ? errorPayload.code || (errorPayload.data ? errorPayload.data.code : "") : "",
			data: errorPayload,
		};

		this.store.dispatch(this.appErrorService.mapErrorTypeToAction(error));
		return throwError(() => error);
	}

	private hookInterceptors<T>(
		response$: Observable<T>,
		request: RequestPayload,
		interceptorType: HttpInterceptorType
	): Observable<T> {
		let current$ = response$;
		if (!this.interceptors[interceptorType]) {
			return current$;
		}
		for (const interceptor of this.interceptors[interceptorType]) {
			current$ = response$.pipe(interceptor(request));
		}
		return current$;
	}

	private process<T>(method: HttpMethod, payload: RequestPayload): Observable<T> {
		const experienceHeader = this.cookieService.get(OBG_EXPERIENCE_SOURCE);

		const headers = pickBy(
			{
				...this.defaultHeaders,
				correlationId: generateVersion4GUID(),
				[OBG_EXPERIMENTS_HEADER_NAME]: this.getExperimentsHeaders(),
				[OBG_EXPERIENCE_HEADER_NAME]: experienceHeader,
				...payload.headers,
			},
			(entry) => !isEmpty(entry)
		) as Record<string, string | string[]>;

		payload = {
			...payload,
			headers,
		};

		const promData = {
			method: method.name.replace("bound ", "").toUpperCase(),
			error: 0,
		};

		const promEnd = this.metrics ? this.metrics.startTimer() : () => undefined;

		const httpCallerOriginalStackTrace = new Error().stack;
		const start = Date.now();

		return of(payload).pipe(
			timeout(300000),
			(payload$) => this.hookInterceptors(payload$, payload, HttpInterceptorType.BeforeRequest),
			switchMap(() => method<T>(payload)),
			(response$) => this.hookInterceptors(response$, payload, HttpInterceptorType.AfterRequest),
			share(),
			catchError((error: HttpErrorResponse | TimeoutError) => {
				if (error instanceof TimeoutError) {
					return this.handleError(
						payload,
						new HttpErrorResponse({
							error: "ERROR: 5 minute-long API wait cut-off has been exceeded!",
							status: 504,
						}),
						httpCallerOriginalStackTrace,
						Date.now() - start
					);
				}
				return this.handleError(payload, error, httpCallerOriginalStackTrace, Date.now() - start);
			}),
			tap({
				complete: () => promEnd(promData),
				error: () => promEnd({ ...promData, error: 1 }),
			})
		);
	}

	private getExperimentsHeaders(): string[] {
		const activeExperiments = this.coreConfig.activeExperiments;
		const result = activeExperiments?.length ? activeExperiments.slice() : [];

		// need to append experiments set up after page is loaded. can happen any time, so it's why need to check before each request
		try {
			const experimentsInCookie = this.cookieService.get(OBG_EXPERIMENTS_SOURCE);
			result.push(...this.readExperimentsFromString(experimentsInCookie));
		} catch (ex) {
			this.logger.error("getExperimentsHeaders", "Error while trying to get dynamic experiments", ex);
		}

		return uniq(result);
	}

	private readExperimentsFromString(input?: string): string[] {
		if (!input) {
			return [];
		}

		return input
			.split(",")
			.map((v) => v.trim())
			.filter((v) => v);
	}

	private generateLogMessage(res: HttpErrorResponse): string {
		const fallBackError = "Server error";
		try {
			const errorResponse = this.generateErrorMsg(res);
			if (!isEmpty(errorResponse.errors)) {
				return `${errorResponse.code}::${errorResponse.errors![0].code}`;
			}

			return errorResponse.code || `Empty Api Error Code::Status Text:${res.statusText}` || fallBackError;
		} catch (e) {
			return `Failed to parse API Error Code::Status Text:${res.statusText} Status::${res.status} Error::${typeof res.error === "object" ? JSON.stringify(res.error) : res.error} Message::${res.message} $`;
		}
	}

	private generateErrorMsg(res: HttpErrorResponse): ApiErrorResponse {
		if (this.platformService.isServer) {
			return JSON.parse(res.error) as ApiErrorResponse;
		}
		return res.error instanceof ProgressEvent
			? ({ code: `PROGRESS_EVENT_ERROR::Error::${res.message}` } as ApiErrorResponse)
			: (JSON.parse(res.error) as ApiErrorResponse);
	}
}
